import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Spinner from "../components/Spinner";
import { IoCloseOutline } from "react-icons/io5";
import { getClinicIdFromToken } from "../../clinic/utils/tokenUtils";
import { useSidebar } from "../../clinic/context/ClinicContext";
import { FaUserDoctor } from "react-icons/fa6";
import { serviceFetchSuggestions } from "../services/api";

const TopSearch = ({
  name,
  value,
  onChange,
  placeholder,
  onSuggestionSelected,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(false); // State to track if a value is selected

  const clinic_id = getClinicIdFromToken();
  const { handleSuggestion, selectedSuggestion } = useSidebar();
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    if (value && value.length < 2) {
      setSuggestions([]);
      setSelected(false);
      return;
    }

    const delayDebounceFn = setTimeout(() => {
      fetchSuggestions();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [value, clinic_id]);

  const fetchSuggestions = async () => {
    if (value) {
      setLoading(true);
      try {
        // Make sure to use the correct URL for your environment
        const response = await serviceFetchSuggestions(value, clinic_id);
        if (response?.status) {
          const data = response?.data;
          if (data) {
            let finalState = combineState(data);
            setSuggestions(finalState);
          } else {
            setSuggestions([]);
          }
        } else {
          throw response;
        }
      } catch (error) {
        console.error("Error fetching autocomplete suggestions:", error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    } else {
      setSuggestions([]);
    }
  };

  useEffect(() => {
    if (selectedSuggestion == null) {
      onChange({ target: { name, value: "" } });
    }
  }, [selectedSuggestion]);

  const handleClearValue = () => {
    onChange({ target: { name, value: "" } });
    setSuggestions([]);
    setSelected(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setSuggestions([]);
        if (!selectedSuggestion) {
          // Only clear the value if selectedSuggestion is null or undefined
          handleClearValue();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedSuggestion]);

  const handleSuggestionClick = (suggestion) => {
    onChange({ target: { name, value: suggestion.phone_number || "" } });

    onSuggestionSelected(suggestion);
    handleSuggestion(suggestion);

    setSuggestions([]);

    setSelected(true);
  };

  const combineState = (currentState) => {
    // Transforming the patients array if it exists
    const transformedPatients = currentState.patients
      ? currentState.patients.map((patient) => ({
          ...patient,
          type: "patient",
        }))
      : [];

    // Transforming the doctors array if it exists
    const transformedDoctors = currentState.doctors
      ? currentState.doctors.map((doctor) => ({
          ...doctor,
          type: "doctor",
        }))
      : [];

    // Merging the transformed arrays
    const transformedState = [...transformedPatients, ...transformedDoctors];
    return transformedState;
  };

  const handleKeyDown = (event) => {
    switch (event.key) {
      case "ArrowUp":
        // Highlight the previous suggestion
        setHighlightedIndex((prevHighlightedIndex) => prevHighlightedIndex - 1);
        break;
      case "ArrowDown":
        // Highlight the next suggestion
        setHighlightedIndex((prevHighlightedIndex) => prevHighlightedIndex + 1);
        break;
      case "Enter":
        // Select the highlighted suggestion
        handleSuggestionClick(suggestions[highlightedIndex]);
        break;
      default:
        break;
    }
  };

  const renderSuggestionWithRelated = (suggestion, index) => (
    <React.Fragment>
      <li
        className={`flex justify-between items-center cursor-pointer select-none relative py-2 pl-3 pr-3 hover:bg-gray-100 ${
          index === highlightedIndex ? "bg-gray-200" : "text-gray-900"
        }`}
        onClick={() => handleSuggestionClick(suggestion)}
      >
        <div className=" inline-flex gap-x-1 items-center">
          {suggestion.patient_id && (
            <span className="text-green-500 w-9">
              {/* Add patient icon here */}
              <img src="../images/patient.png" />
            </span>
          )}
          {suggestion.doctor_id && (
            <div className=" w-9 h-9 rounded-full mx-auto flex justify-center items-center text-xl border border-gray-50 bg-gray-100 text-[#04225b]">
              <FaUserDoctor />
            </div>
          )}
          <span className="font-medium truncate">{suggestion.full_name}</span>
        </div>
        <span className="text-blue-300 truncate">
          {suggestion.phone_number}
        </span>
      </li>
      {suggestion.related_patients &&
        suggestion.related_patients.length > 0 &&
        suggestion.related_patients.map((related, index) => (
          <li
            key={`related-${index}`}
            className={`flex justify-between items-center cursor-pointer select-none relative py-2 pl-3 pr-3 hover:bg-gray-100 ${
              index === highlightedIndex ? "bg-gray-200" : "text-gray-900"
            }`}
            onClick={() => handleSuggestionClick(related)}
          >
            <span className="font-medium truncate">{related.full_name}</span>
            {/* You can add an icon or additional element here to signify a related patient if needed */}
          </li>
        ))}
    </React.Fragment>
  );

  return (
    <div className={` relative w-full autocomplete-container `}>
      <input
        type="text"
        name={name}
        value={value}
        onChange={(e) => onChange(e)}
        onKeyDown={(e) => handleKeyDown(e)}
        placeholder={placeholder}
        className="block w-full bg-gray-50 text-gray-700 border border-gray-300 rounded-md py-2 px-4 leading-tight focus:outline-none focus:bg-white focus:border-blue-500"
        autoComplete="off"
      />
      {selected && value && (
        <button
          onClick={handleClearValue}
          className="inline-flex items-center px-1 py-1 rounded-full absolute right-2 top-1.5  transition ease-in-out duration-150  bg-indigo-400 text-white"
        >
          <IoCloseOutline />
        </button>
      )}
      <span className=" absolute right-0 top-0">{loading && <Spinner />}</span>

      {suggestions.length > 0 && (
        <ul
          ref={suggestionsRef}
          className={` absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm
            ${selected ? " hidden" : ""}`}
        >
          {suggestions.map((suggestion, index) =>
            renderSuggestionWithRelated(suggestion)
          )}
        </ul>
      )}
    </div>
  );
};

const TopSearchHeader = ({
  patient,
  setPatient,
  patientDetails,
  setPatientDetails,
}) => {
  // Handle field changes for each input
  const handlePatientChange = (e) => {
    setPatient(e.target.value);
  };

  const handleSuggestionSelected = (suggestion) => {
    setPatientDetails({
      ...patientDetails,
      patient_id: suggestion.patient_id,
      phone_number: suggestion.phone_number,
      name: suggestion.full_name,
    });
  };

  return (
    <div className="w-full max-w-4xl px-3">
      <TopSearch
        placeholder="Search for a patient/doctor"
        value={patient}
        onChange={handlePatientChange}
        onSuggestionSelected={handleSuggestionSelected}
        className="w-full p-2 border border-gray-300 rounded-md"
      />
      {/* Add other inputs for patient details here, using handleDetailsChange */}
    </div>
  );
};

export default TopSearchHeader;
